export interface GeocodedLatLng {
  lat: number;
  lng: number;
}

/**
 * Resolves a free-text address to coordinates using the Google Maps JS Geocoder.
 *
 * Requires the Maps script to already be loaded (any mounted `RideMapBackdrop`
 * loads it via `APIProvider`). Used to resolve address-book entries that came
 * back from `getRideLocations` without `lat`/`lng` (never geocoded when saved).
 *
 * @param address - Free-text address to geocode.
 * @returns The first matching `{ lat, lng }` result.
 * @returns `undefined` if the Maps script isn't loaded yet, no result is found,
 *   or the geocode request fails.
 */
export async function geocodeAddress(address: string): Promise<GeocodedLatLng | undefined> {
  if (typeof google === 'undefined' || !google.maps?.Geocoder) {
    console.warn('geocodeAddress: Google Maps script not loaded yet');
    return undefined;
  }

  try {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ address });
    const location = results[0]?.geometry?.location;

    if (!location) {
      console.warn(`geocodeAddress: no result for address "${address}"`);
      return undefined;
    }

    return { lat: location.lat(), lng: location.lng() };
  } catch (error) {
    console.warn(`geocodeAddress: request failed for address "${address}":`, error);
    return undefined;
  }
}
